import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import Navbar from './components/layout/Navbar'
import Footer from './components/layout/Footer'
import LoadingSpinner from './components/common/LoadingSpinner'
import ProtectedRoute from './components/common/ProtectedRoute'
import Seo from './components/common/Seo'
import SiteChrome from './components/common/SiteChrome'
import GoogleAnalytics from './components/common/GoogleAnalytics'
import { useSiteContent } from './content/useSiteContent'
import useAuthStore from './store/auth.store'
import WhatsAppWidget from './components/common/WhatsAppWidget'
import MobileBottomNav from './components/layout/MobileBottomNav'
// import AIStylistChat from './components/ai/AIStylistChat'

// Lazy-loaded pages
const HomePage = lazy(() => import('./pages/HomePage'))
const ShopPage = lazy(() => import('./pages/ShopPage'))
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'))
const VirtualTryOnPage = lazy(() => import('./pages/VirtualTryOnPage'))
const CartPage = lazy(() => import('./pages/CartPage'))
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'))
const OrderTrackingPage = lazy(() => import('./pages/OrderTrackingPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const OrdersPage = lazy(() => import('./pages/OrdersPage'))
const AddressesPage = lazy(() => import('./pages/AddressesPage'))
const PrescriptionsPage = lazy(() => import('./pages/PrescriptionsPage'))
const WishlistPage = lazy(() => import('./pages/WishlistPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'))
const AdminProductsPage = lazy(() => import('./pages/AdminProductsPage'))
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage'))
const StoresPage = lazy(() => import('./pages/StoresPage'))
const StoreDetailPage = lazy(() => import('./pages/StoreDetailPage'))
const RequestSpecsPage = lazy(() => import('./pages/RequestSpecsPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const ReturnsPage = lazy(() => import('./pages/ReturnsPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const HelpPage = lazy(() => import('./pages/HelpPage'))
const BlogPage = lazy(() => import('./pages/BlogPage'))
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'))
const FaqPage = lazy(() => import('./pages/FaqPage'))
const CustomPage = lazy(() => import('./pages/CustomPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'))

// Pages that don't use the standard layout
const AUTH_ROUTES = ['/login', '/register']

function AppLayout({ children }) {
  return (
    <div className="min-h-screen bg-color-bg font-inter flex flex-col">
      <Navbar />
      <main className="flex-1" style={{ paddingTop: 'var(--header-h, 130px)' }}>
        {children}
      </main>
      <Footer />
      {/* <AIStylistChat /> */}
      <WhatsAppWidget />
      <MobileBottomNav />
    </div>
  )
}

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen bg-color-bg font-inter">
      {children}
    </div>
  )
}

const STAFF_ROLES = ['super_admin', 'marketing_admin', 'product_manager', 'order_manager']

// While maintenance mode is on (dashboard → Website settings) visitors get the maintenance page. Signed-in staff see the
// real store with a reminder bar, and /login stays open so staff can sign in.
function MaintenanceGate({ children }) {
  const { site } = useSiteContent()
  const role = useAuthStore((s) => s.user?.role)
  const { pathname } = useLocation()
  const m = site.maintenance
  if (!m?.enabled) return children
  if (STAFF_ROLES.includes(role)) {
    return (
      <>
        <div className="fixed inset-x-0 bottom-0 z-[100] bg-black px-4 py-2 text-center text-xs font-medium text-white" role="status" data-testid="maintenance-staff-bar">
          Maintenance mode is ON — visitors can’t see the store. Only staff who are signed in can.
        </div>
        {children}
      </>
    )
  }
  if (pathname === '/login') return children
  return <MaintenancePage title={m.title} message={m.message} />
}

export default function App() {
  return (
    <Suspense fallback={<LoadingSpinner variant="page" />}>
      <Seo />
      <SiteChrome />
      <GoogleAnalytics />
      <MaintenanceGate>
      <Routes>
        {/* Auth Routes - no navbar/footer */}
        <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
        <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />

        {/* Main Layout Routes */}
        <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
        <Route path="/shop" element={<AppLayout><ShopPage /></AppLayout>} />
        <Route path="/product/:slug" element={<AppLayout><ProductDetailPage /></AppLayout>} />
        <Route path="/try-on" element={<AppLayout><VirtualTryOnPage /></AppLayout>} />
        <Route path="/stores" element={<AppLayout><StoresPage /></AppLayout>} />
        <Route path="/stores/:slug" element={<AppLayout><StoreDetailPage /></AppLayout>} />
        <Route path="/request-specs" element={<AppLayout><RequestSpecsPage /></AppLayout>} />
        <Route path="/contact" element={<AppLayout><ContactPage /></AppLayout>} />
        <Route path="/terms" element={<AppLayout><TermsPage /></AppLayout>} />
        <Route path="/returns" element={<AppLayout><ReturnsPage /></AppLayout>} />
        <Route path="/privacy" element={<AppLayout><PrivacyPage /></AppLayout>} />
        <Route path="/about" element={<AppLayout><AboutPage /></AppLayout>} />
        <Route path="/help" element={<AppLayout><HelpPage /></AppLayout>} />
        <Route path="/blog" element={<AppLayout><BlogPage /></AppLayout>} />
        <Route path="/blog/:slug" element={<AppLayout><BlogPostPage /></AppLayout>} />
        <Route path="/faqs" element={<AppLayout><FaqPage /></AppLayout>} />
        {/* Nav entry point; the prescriptions page itself requires sign-in */}
        <Route path="/upload-prescription" element={<Navigate to="/account/prescriptions" replace />} />
        <Route path="/cart" element={<AppLayout><CartPage /></AppLayout>} />
        <Route path="/wishlist" element={<AppLayout><WishlistPage /></AppLayout>} />

        {/* Protected Customer Routes */}
        <Route path="/checkout" element={
          <AppLayout>
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          </AppLayout>
        } />
        <Route path="/order/:id" element={
          <AppLayout>
            <ProtectedRoute>
              <OrderTrackingPage />
            </ProtectedRoute>
          </AppLayout>
        } />
        <Route path="/account" element={
          <AppLayout>
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          </AppLayout>
        } />
        <Route path="/account/orders" element={
          <AppLayout>
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          </AppLayout>
        } />
        <Route path="/account/addresses" element={
          <AppLayout>
            <ProtectedRoute>
              <AddressesPage />
            </ProtectedRoute>
          </AppLayout>
        } />
        <Route path="/account/prescriptions" element={
          <AppLayout>
            <ProtectedRoute>
              <PrescriptionsPage />
            </ProtectedRoute>
          </AppLayout>
        } />
        <Route path="/account/wishlist" element={<Navigate to="/wishlist" replace />} />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <AuthLayout>
            <ProtectedRoute requiredRole="super_admin">
              <AdminDashboardPage />
            </ProtectedRoute>
          </AuthLayout>
        } />
        <Route path="/admin/products" element={
          <AuthLayout>
            <ProtectedRoute requiredRole="super_admin">
              <AdminProductsPage />
            </ProtectedRoute>
          </AuthLayout>
        } />
        <Route path="/admin/orders" element={
          <AuthLayout>
            <ProtectedRoute requiredRole="super_admin">
              <AdminOrdersPage />
            </ProtectedRoute>
          </AuthLayout>
        } />

        {/* Pages created in the admin dashboard live at /their-address; unknown ones fall through to the 404 page */}
        <Route path="/:slug" element={<AppLayout><CustomPage /></AppLayout>} />

        {/* 404 */}
        <Route path="*" element={<AppLayout><NotFoundPage /></AppLayout>} />
      </Routes>
      </MaintenanceGate>
    </Suspense>
  )
}
